import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { 
  validateImportFile, 
  checkExistingData, 
  importMenu 
} from '@/lib/services/menuImport';
import type { 
  ImportProgress, 
  ImportPreview, 
  ImportResult 
} from '@/lib/services/menuImport';
import type { ExportData } from '@/lib/services/menuExport';
import type { Restaurant } from '@/types';
import { 
  Upload, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  FileArchive,
  AlertTriangle
} from 'lucide-react';

interface MenuImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurant: Restaurant;
  onImportComplete?: () => void;
}

type ImportStage = 'upload' | 'preview' | 'importing' | 'complete' | 'error';

/**
 * MenuImportModal - Handle menu import with validation and preview
 * Follows the app's design patterns and theme
 */
export const MenuImportModal: React.FC<MenuImportModalProps> = ({
  isOpen,
  onClose,
  restaurant,
  onImportComplete
}) => {
  const [stage, setStage] = useState<ImportStage>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importData, setImportData] = useState<ExportData | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setValidationError(null);
    setStage('upload');

    // Validate the file
    try {
      const validation = await validateImportFile(file);
      
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid file');
        return;
      }

      if (validation.data && validation.preview) {
        setImportData(validation.data);
        
        // Check for existing data
        const existingData = await checkExistingData(restaurant.id);
        const enhancedPreview = {
          ...validation.preview,
          hasExistingData: {
            digitalMenu: existingData.hasDigitalMenu,
            imageMenu: existingData.hasImageMenu
          }
        };
        
        setPreview(enhancedPreview);
        setStage('preview');
      }
    } catch (error) {
      console.error('File validation error:', error);
      setValidationError('Failed to process file. Please try again.');
    }
  }, [restaurant.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip']
    },
    maxFiles: 1,
    disabled: isImporting
  });

  const handleImport = async () => {
    if (!uploadedFile || !importData) return;

    setIsImporting(true);
    setImportError(null);
    setProgress(null);
    setStage('importing');

    try {
      const result = await importMenu(
        uploadedFile,
        importData,
        restaurant,
        (progressData) => setProgress(progressData)
      );

      setImportResult(result);
      
      if (result.success) {
        setStage('complete');
        onImportComplete?.();
      } else {
        setImportError(result.errors.join(', ') || 'Import failed');
        setStage('error');
      }
    } catch (error) {
      console.error('Import failed:', error);
      setImportError(error instanceof Error ? error.message : 'Import failed');
      setStage('error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      // Reset all state
      setStage('upload');
      setUploadedFile(null);
      setValidationError(null);
      setImportData(null);
      setPreview(null);
      setProgress(null);
      setImportResult(null);
      setImportError(null);
      onClose();
    }
  };

  const handleStartOver = () => {
    setStage('upload');
    setUploadedFile(null);
    setValidationError(null);
    setImportData(null);
    setPreview(null);
    setProgress(null);
    setImportResult(null);
    setImportError(null);
  };

  const getStageIcon = (stageName: string) => {
    switch (stageName) {
      case 'validating':
      case 'clearing_existing':
      case 'uploading_images':
      case 'importing_data':
        return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
      case 'complete':
        return <CheckCircle2 className="w-5 h-5 text-apple-green" />;
      default:
        return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;
    }
  };

  const getModalTitle = () => {
    switch (stage) {
      case 'upload':
        return 'Import Menu';
      case 'preview':
        return 'Import Preview';
      case 'importing':
        return 'Importing Menu';
      case 'complete':
        return 'Import Complete';
      case 'error':
        return 'Import Failed';
      default:
        return 'Import Menu';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      size="lg"
      closeOnOverlayClick={!isImporting}
      closeOnEscape={!isImporting}
    >
      <div className="space-y-6">
        <AnimatePresence mode="wait">
          {/* Upload Stage */}
          {stage === 'upload' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                      Important Warning
                    </h4>
                    <p className="text-sm text-yellow-800">
                      Importing will <strong>completely replace</strong> all menu data AND restaurant details (name, description, logo, theme, contact info) for "{restaurant.name}". 
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary bg-primary bg-opacity-10'
                    : validationError
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <FileArchive className="w-12 h-12 text-gray-400" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {isDragActive ? 'Drop your menu export file here' : 'Upload Menu Export'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select or drag & drop your exported menu ZIP file
                    </p>
                  </div>
                  
                  <Button
                    variant="primary"
                    leftIcon={<Upload className="w-4 h-4" />}
                    size="sm"
                  >
                    Choose File
                  </Button>
                </div>
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-apple-red mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-900 mb-1">
                        Invalid File
                      </h4>
                      <p className="text-sm text-red-800">
                        {validationError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* File Info */}
              {uploadedFile && !validationError && (
                <div className="bg-apple-green bg-opacity-10 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-apple-green mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-green-900 mb-1">
                        File Ready for Import
                      </h4>
                      <p className="text-sm text-green-800">
                        {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Preview Stage */}
          {stage === 'preview' && preview && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Restaurant Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Restaurant Data</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Name:</strong> {preview.restaurant.name}</p>
                  {preview.restaurant.description && (
                    <p><strong>Description:</strong> {preview.restaurant.description}</p>
                  )}
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Restaurant details (name, description, logo, etc.) will also be imported and will replace current restaurant information.
                  </p>
                </div>
              </div>

              {/* Menu Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Digital Menu</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>{preview.digitalMenuSummary.sectionsCount} sections</p>
                    <p>{preview.digitalMenuSummary.itemsCount} menu items</p>
                    {preview.digitalMenuSummary.sectionNames.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">Sections:</p>
                        <p className="text-xs">{preview.digitalMenuSummary.sectionNames.join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-xl p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Image Menu</h4>
                  <div className="text-sm text-purple-800">
                    <p>{preview.imageMenuSummary.imagesCount} images</p>
                  </div>
                </div>
              </div>

              {/* Existing Data Warning */}
              {(preview.hasExistingData.digitalMenu || preview.hasExistingData.imageMenu) && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-apple-red mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-900 mb-2">
                        Existing Data Will Be Replaced
                      </h4>
                      <div className="text-sm text-red-800 space-y-1">
                        {preview.hasExistingData.digitalMenu && (
                          <p>• Current digital menu will be completely replaced</p>
                        )}
                        {preview.hasExistingData.imageMenu && (
                          <p>• Current image menu will be completely replaced</p>
                        )}
                        <p className="font-semibold mt-2">This action cannot be undone!</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Importing Stage */}
          {stage === 'importing' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Please don't close this window while the import is in progress.
                </p>
              </div>

              {/* Progress */}
              {progress && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">
                      {progress.message}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {Math.round(progress.progress)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <motion.div
                      className="bg-primary h-3 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  
                  {progress.currentItem && (
                    <p className="text-xs text-gray-500 truncate">
                      {progress.currentItem}
                    </p>
                  )}

                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                    {getStageIcon(progress.stage)}
                    <span className="text-sm font-medium text-gray-700">
                      {progress.message}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Success Stage */}
          {stage === 'complete' && importResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center space-y-6"
            >
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-apple-green bg-opacity-10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-apple-green" />
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Import Completed Successfully!
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your menu has been imported and is now available.
                </p>
                
                {/* Import Summary */}
                <div className="bg-apple-green bg-opacity-10 rounded-xl p-4 text-left">
                  <h4 className="font-semibold text-green-900 mb-2">Import Summary</h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>✓ {importResult.importedSections} sections imported</p>
                    <p>✓ {importResult.importedItems} menu items imported</p>
                    <p>✓ {importResult.importedImageMenuItems} images imported</p>
                    
                    {importResult.errors.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <p className="font-semibold text-green-900">Warnings:</p>
                        {importResult.errors.map((error, index) => (
                          <p key={index} className="text-xs text-green-700">• {error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Error Stage */}
          {stage === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-apple-red" />
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Import Failed
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  There was an error importing your menu. Please try again.
                </p>
                
                {importError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left">
                    <h4 className="font-semibold text-red-900 mb-2">Error Details</h4>
                    <p className="text-sm text-red-800">
                      {importError}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isImporting}
            className="flex-1"
          >
            {stage === 'complete' ? 'Close' : 'Cancel'}
          </Button>
          
          {stage === 'preview' && (
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={isImporting}
              className="flex-1"
            >
              Confirm Import
            </Button>
          )}
          
          {stage === 'error' && (
            <Button
              variant="primary"
              onClick={handleStartOver}
              className="flex-1"
            >
              Try Again
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
